import { Dispatch, SetStateAction, WheelEvent } from "react";
import { api } from "@/helper/api";

export const mapOverviewResultsToRows = (results: any[]) => {
  const rowMap: Record<string, any> = {};
  results.forEach((item) => {
    const id = item.company_subject_id;

    if (!rowMap[id]) {
      rowMap[id] = {
        id,
        name: item.company_name,
        is_parent: !!item.is_parent,
        is_draft: item.draft_mode === true,
      };
    }

    if (item.values && Array.isArray(item.values)) {
      item.values.forEach((valueItem: any) => {
        const templateId =
          valueItem?.template_id ?? valueItem?.template?.id ?? valueItem?.template;
        const valueKey = `value_${templateId}`;
        const anchorDataIdKey = `anchor_data_id_${templateId}`;
        const raw = valueItem?.value;
        const inheritanceInfo = valueItem?.inheritance_info;

        if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
          rowMap[id][valueKey] = raw;
        } else {
          const numeric =
            typeof raw === "string" && !isNaN(raw as any) ? parseFloat(raw) : raw;
          rowMap[id][valueKey] = numeric;
        }
        const anchorDataId = valueItem?.anchor_data_id ?? valueItem?.id;
        if (anchorDataId) rowMap[id][anchorDataIdKey] = anchorDataId;
        rowMap[id][`is_inherited_${templateId}`] = !!inheritanceInfo?.is_inherited;
        if (inheritanceInfo?.inherited_from_subject) {
          rowMap[id][`inherited_from_${templateId}`] =
            inheritanceInfo.inherited_from_subject;
        }
        const inheritedValue =
          inheritanceInfo?.inherited_value ??
          (inheritanceInfo?.is_inherited ? rowMap[id][valueKey] : undefined);
        if (inheritedValue !== undefined) {
          rowMap[id][`inherited_value_${templateId}`] = inheritedValue;
        }
      });
    }
  });
  return Object.values(rowMap);
};

export const fetchTemplateTypes = async (
  selectedTemplates: any[],
  anchorDataTemplateDetails: Record<string, any>,
  setAnchorDataTemplateDetails: Dispatch<
    SetStateAction<Record<string, any>>
  >,
  setAnchorDataTemplateTypes: Dispatch<
    SetStateAction<Record<string, string>>
  >,
) => {
  const validTemplates = selectedTemplates.filter(Boolean);
  if (validTemplates.length === 0) return;

  const typePromises = validTemplates.map(
    (template) =>
      new Promise<void>((resolve) => {
        if (anchorDataTemplateDetails[template.value]) {
          const cachedType = anchorDataTemplateDetails[template.value]?.type;
          if (cachedType) {
            setAnchorDataTemplateTypes((prev) => ({
              ...prev,
              [template.value]: cachedType,
            }));
          }
          resolve();
          return;
        }

        api.genericApiRequest({
          method: "get",
          entity: `anchorDataTemplate/${template.value}`,
          parametersToRender: {
            depth: "0",
          },
          successHandler: (resp: any) => {
            const type = resp.data.type;
            setAnchorDataTemplateDetails((prev) => ({
              ...prev,
              [template.value]: resp.data,
            }));
            setAnchorDataTemplateTypes((prev) => ({
              ...prev,
              [template.value]: type,
            }));
            resolve();
          },
          failHandler: (err: any) => {
            console.error(
              `Failed to fetch type for template ${template.value}:`,
              err,
            );
            resolve();
          },
        });
      }),
  );

  await Promise.all(typePromises);
};

export const handleAnchorDataTemplateChange = (
  selectedOption: any,
  idx: number,
  setSelectedAnchorDataTemplates: Dispatch<SetStateAction<(any | null)[]>>,
  setLastSelectedTemplates: Dispatch<SetStateAction<(any | null)[]>>,
) => {
  if (selectedOption === undefined) return;
  setSelectedAnchorDataTemplates((prev) => {
    if (prev[idx] === selectedOption) return prev;
    const next = [...prev];
    next[idx] = selectedOption;
    return next;
  });
  setLastSelectedTemplates((prev) => {
    if (!selectedOption || prev[idx] === selectedOption) return prev;
    const next = [...prev];
    if (selectedOption) next[idx] = selectedOption;
    return next;
  });
};

export const formatObjectListEntries = (
  value: any[],
  fieldOrder?: string[],
  t: (key: string) => string = (k) => k,
): string | JSX.Element => {
  const formattedEntries = value.map((obj: any, idx: number) => {
    if (typeof obj === "object" && obj !== null) {
      const orderedValues =
        Array.isArray(fieldOrder) && fieldOrder.length > 0
          ? fieldOrder
            .map((field) => obj?.[field])
            .filter((val) => val !== null && val !== "" && val !== undefined)
            .map((val) => String(val))
          : Object.values(obj)
            .filter((val) => val !== null && val !== "" && val !== undefined)
            .map((val) => String(val));

      const joined = orderedValues.join(", ");
      return joined ? `[${t("entry")} ${idx + 1}] ${joined}` : `[${t("entry")} ${idx + 1}]`;
    }
    return String(obj);
  });

  return (
    <>
      {formattedEntries.map((entry, idx) => (
        <span key={idx}>
          {entry}
          {idx < formattedEntries.length - 1 && <br />}
        </span>
      ))}
    </>
  );
};

export const percentInputToDecimalNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  const numeric = Number(normalized);
  return Number.isNaN(numeric) ? null : numeric;
};

export const percentValueToDisplayString = (
  value: string | number,
): string | null => {
  const normalized = String(value).trim().replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;
  return String(value).trim();
};

export const formatGermanDate = (value: unknown): string | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getDate()).padStart(2, "0");
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const year = value.getFullYear();
    return `${day}.${month}.${year}`;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${day}.${month}.${year}`;
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const day = String(parsed.getDate()).padStart(2, "0");
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const year = parsed.getFullYear();
      return `${day}.${month}.${year}`;
    }
  }
  return null;
};

const formatListEntryForDisplay = (entry: any): string => {
  if (entry == null) return "";
  if (typeof entry !== "object") return String(entry);
  const label =
    entry.label ?? entry.name ?? entry.value ?? entry.code ?? entry.id;
  if (label != null) return String(label);
  const parts = Object.values(entry).filter((v) => v != null && v !== "");
  return parts.length ? parts.map(String).join(", ") : "";
};

export const formatInheritedForDisplay = (value: any, type: string): string => {
  if (value == null) return "";
  if (type === "percent") {
    return percentValueToDisplayString(value) ?? String(value);
  }
  if (type === "date") {
    if (typeof value === "string") return value;
    return String(value);
  }
  if (
    type === "subject_id_list" ||
    type === "wz_code_id_list" ||
    type === "object_list"
  ) {
    if (Array.isArray(value)) {
      const formatted = value
        .map(formatListEntryForDisplay)
        .filter(Boolean)
        .join(", ");
      return formatted || "-";
    }
    if (typeof value === "object") {
      return formatListEntryForDisplay(value) || "-";
    }
  }
  if (Array.isArray(value)) {
    return value
      .map((entry: any) => {
        if (entry && typeof entry === "object") {
          return (
            entry.label ?? entry.name ?? entry.value ?? entry.code ?? entry.id ?? null
          );
        }
        return entry;
      })
      .filter(Boolean)
      .map(String)
      .join(", ");
  }
  if (typeof value === "object") {
    return String(value.label ?? value.name ?? value.value ?? value.id ?? "");
  }
  return String(value);
};

export const inheritedValueToSubjectIdListOptions = (value: any): any[] => {
  if (!Array.isArray(value)) {
    if (value && typeof value === "object")
      return [{ value: value.id ?? value.value, label: value.name ?? value.label ?? String(value.id ?? value.value ?? "") }];
    return [];
  }
  return value
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const v = entry.value ?? entry.id;
      const l = entry.label ?? entry.name ?? (v != null ? String(v) : "");
      return v != null ? { value: String(v), label: String(l) } : null;
    })
    .filter(Boolean);
};

export const inheritedValueToWzCodeIdListOptions = (value: any): any[] => {
  if (!Array.isArray(value)) {
    if (value && typeof value === "object") {
      const v = value.value ?? value.id;
      const label =
        value.label ??
        (() => {
          const code = value.code ?? value.name ?? value.id;
          const desc = value.description ?? "";
          return desc ? `${code} - ${desc}` : String(code ?? "");
        })();
      return v != null ? [{ value: String(v), label: String(label) }] : [];
    }
    return [];
  }
  return value
    .map((entry: any) => {
      if (!entry || typeof entry !== "object") return null;
      const v = entry.value ?? entry.id;
      const label =
        entry.label ??
        (() => {
          const code = entry.code ?? entry.name ?? entry.id;
          const desc = entry.description ?? "";
          return desc ? `${code} - ${desc}` : String(code ?? "");
        })();
      return v != null ? { value: String(v), label: String(label) } : null;
    })
    .filter(Boolean);
};

export const normalizePhoneForComparison = (value: any): string => {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (s === "") return "";
  if (/^\+\d{1,3}\s*$/.test(s)) return "";
  return s;
};

const DEFAULT_PHONE_CALLING_CODE = "49";

export const normalizePastedPhoneNumber = (
  pasted: string,
  defaultCallingCode: string = DEFAULT_PHONE_CALLING_CODE,
): string => {
  const trimmed = String(pasted ?? "").trim();
  if (trimmed === "") return "";

  if (/^0+/.test(trimmed)) {
    const national = trimmed.replace(/^0+/, "").trim();
    return national ? `+${defaultCallingCode} ${national}` : `+${defaultCallingCode}`;
  }
  if (/^00\d/.test(trimmed)) {
    return `+${trimmed.replace(/^00/, "").trim()}`;
  }
  if (/^\+\d/.test(trimmed)) {
    return trimmed;
  }
  return "";
};

export const normalizeForComparison = (value: any, type: string): any => {
  if (value === undefined || value === "") return null;
  if (value === null) return null;

  if (type === "int" || type === "float" || type === "percent" || type === "price") {
    const normalized =
      typeof value === "number" ? value : Number(String(value).replace(",", "."));
    return Number.isNaN(normalized) ? null : normalized;
  }

  if (type === "subject_id_list" || type === "wz_code_id_list") {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry: any) => {
        if (entry && typeof entry === "object") {
          return String(entry.value ?? entry.id ?? entry.code ?? entry.name ?? "");
        }
        return String(entry);
      })
      .filter(Boolean);
  }

  if (type === "object_list") {
    if (!Array.isArray(value)) return null;
    const normalized = value.map((obj: any) => {
      if (obj == null || typeof obj !== "object") return null;
      const sorted: Record<string, string> = {};
      Object.keys(obj)
        .sort()
        .forEach((k) => {
          const v = obj[k];
          sorted[k] = v == null ? "" : String(v);
        });
      return JSON.stringify(sorted);
    });
    return JSON.stringify(normalized.sort());
  }

  if (type === "phone_number") {
    const phoneNorm = normalizePhoneForComparison(value);
    return phoneNorm === "" ? null : phoneNorm;
  }

  if (type === "enum" || type === "date") {
    return String(value);
  }

  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }

  return value;
};

export const isSameAnchorDataValue = (
  nextValue: any,
  initialValue: any,
  type: string,
): boolean => {
  const normalizedNext = normalizeForComparison(nextValue, type);
  const normalizedInitial = normalizeForComparison(initialValue, type);
  return JSON.stringify(normalizedNext) === JSON.stringify(normalizedInitial);
};

export const applyInlineEditsToOverviewRows = (
  rows: any[],
  subjectIds: string[],
  pendingInlineEdits: Record<string, Record<string, any>>,
  pendingResetToInherited: Record<string, Record<string, boolean>>,
): any[] => {
  const subjectIdSet = new Set(subjectIds);
  return rows.map((row) => {
    if (!subjectIdSet.has(row.id)) return row;

    const edits = pendingInlineEdits[row.id] ?? {};
    const resets = pendingResetToInherited[row.id] ?? {};
    const columnKeys = Array.from(
      new Set([...Object.keys(edits), ...Object.keys(resets)]),
    );
    if (columnKeys.length === 0) return row;

    let nextRow = { ...row };
    for (const columnKey of columnKeys) {
      const templateId = columnKey.replace(/^value_/, "");
      const isInheritedKey = `is_inherited_${templateId}`;
      const inheritedValueKey = `inherited_value_${templateId}`;

      if (resets[columnKey]) {
        nextRow = {
          ...nextRow,
          [columnKey]: row[inheritedValueKey] ?? null,
          [isInheritedKey]: true,
        };
      } else if (edits[columnKey] !== undefined) {
        nextRow = {
          ...nextRow,
          [columnKey]: edits[columnKey],
          [isInheritedKey]: false,
        };
      }
    }
    return nextRow;
  });
};

export const blurNumberInputOnWheel = (e: WheelEvent<HTMLInputElement>) =>
  e.currentTarget.blur();
